// AUTO-GENERATED from config/journey-protocol.json by scripts/generate-journey-protocol.mjs.
// DO NOT EDIT BY HAND. Run `node scripts/generate-journey-protocol.mjs` to regenerate; CI fails on drift.
//
// The Co-Drive journey protocol (docs/FRAMEWORK-computer-use-co-drive.md): the shared
// vocabulary for who holds the wheel, what's blocking, and what happened. One of three
// identical twins (web client / functions / browser-service) — the SSOT is the JSON.

export const SSOT_VERSION = "2026-09-02a";

// ── The Wheel ─────────────────────────────────────────────────────────────
export type ControlState = "ai_acting" | "awaiting_approval" | "needs_human" | "human_acting" | "verifying" | "paused_by_user" | "suspended" | "done" | "blocked";

export const CONTROL_STATES: Readonly<Record<ControlState, { readonly terminal: boolean }>> = {
  "ai_acting": { terminal: false },
  "awaiting_approval": { terminal: false },
  "needs_human": { terminal: false },
  "human_acting": { terminal: false },
  "verifying": { terminal: false },
  "paused_by_user": { terminal: false },
  "suspended": { terminal: false },
  "done": { terminal: true },
  "blocked": { terminal: true },
} as const;

export const TRANSITIONS: Readonly<Record<ControlState, readonly ControlState[]>> = {
  "ai_acting": ["needs_human", "verifying", "paused_by_user", "human_acting", "awaiting_approval", "blocked", "done"],
  "awaiting_approval": ["ai_acting", "needs_human", "paused_by_user", "blocked"],
  "needs_human": ["verifying", "paused_by_user", "human_acting", "suspended", "blocked"],
  "human_acting": ["verifying", "paused_by_user", "blocked"],
  "verifying": ["ai_acting", "needs_human", "paused_by_user", "done", "blocked"],
  "paused_by_user": ["verifying", "blocked"],
  "suspended": ["verifying", "needs_human", "paused_by_user", "blocked"],
  "done": [],
  "blocked": [],
} as const;

export type TransitionReason = "journey_created" | "pilot_dispatch" | "wall_detected" | "pilot_stuck" | "observed_completion" | "human_attested" | "pause" | "resume" | "take_over" | "hand_back" | "verdict_recorded" | "session_ended" | "surface_closed" | "superseded" | "abandoned_ttl" | "max_age";

export const TRANSITION_REASONS: readonly TransitionReason[] = ["journey_created", "pilot_dispatch", "wall_detected", "pilot_stuck", "observed_completion", "human_attested", "pause", "resume", "take_over", "hand_back", "verdict_recorded", "session_ended", "surface_closed", "superseded", "abandoned_ttl", "max_age"] as const;

// ── Walls ─────────────────────────────────────────────────────────────────
export type WallType = "credentials" | "twofa" | "captcha" | "payment" | "consent" | "challenge_wall" | "reauth" | "ambiguity" | "error";

export const WALL_TYPES: Readonly<Record<WallType, { readonly actor: "human"; readonly blackout: boolean }>> = {
  "credentials": { actor: "human", blackout: true },
  "twofa": { actor: "human", blackout: true },
  "captcha": { actor: "human", blackout: true },
  "payment": { actor: "human", blackout: true },
  "consent": { actor: "human", blackout: false },
  "challenge_wall": { actor: "human", blackout: false },
  "reauth": { actor: "human", blackout: true },
  "ambiguity": { actor: "human", blackout: false },
  "error": { actor: "human", blackout: false },
} as const;

// ── Page kinds (the shipped observation vocabulary) ───────────────────────
export type PageKind = "login" | "2fa" | "challenge" | "search" | "signedin" | "other" | "notfound" | "signup";

export const PAGE_KINDS: readonly PageKind[] = ["login", "2fa", "challenge", "search", "signedin", "other", "notfound", "signup"] as const;

export const PAGE_KIND_TO_WALL: Readonly<Record<PageKind, WallType | null>> = {
  "login": "credentials",
  "2fa": "twofa",
  "challenge": "captcha",
  "search": null,
  "signedin": null,
  "other": null,
  "notfound": "error",
  "signup": "credentials",
} as const;

// ── Directives ────────────────────────────────────────────────────────────
export type DirectiveKind = "point" | "spotlight" | "narrate" | "confirm" | "status";

export const DIRECTIVE_KINDS: readonly DirectiveKind[] = ["point", "spotlight", "narrate", "confirm", "status"] as const;

export type DirectiveAffordance = "none" | "arrow" | "circle";

export const DIRECTIVE_AFFORDANCES: readonly DirectiveAffordance[] = ["none", "arrow", "circle"] as const;

// ── Verbs + the actuator interlock classes ────────────────────────────────
export type Verb = "navigate" | "click" | "scroll" | "screenshot" | "evaluate" | "waitFor" | "type" | "fill" | "press" | "selectOption" | "setInputFiles";

export type RiskClass = "navigate" | "observe" | "act";

export const VERBS: Readonly<Record<Verb, { readonly riskClass: RiskClass }>> = {
  "navigate": { riskClass: "navigate" },
  "click": { riskClass: "navigate" },
  "scroll": { riskClass: "navigate" },
  "screenshot": { riskClass: "observe" },
  "evaluate": { riskClass: "observe" },
  "waitFor": { riskClass: "observe" },
  "type": { riskClass: "act" },
  "fill": { riskClass: "act" },
  "press": { riskClass: "act" },
  "selectOption": { riskClass: "act" },
  "setInputFiles": { riskClass: "act" },
} as const;

// ── Receipts + journey types ──────────────────────────────────────────────
export type ReceiptKind = "journey_created" | "control_transition" | "wall_encountered" | "wall_cleared" | "directive_issued" | "navigate" | "pilot_step" | "verdict" | "journey_closed";

export const RECEIPT_KINDS: readonly ReceiptKind[] = ["journey_created", "control_transition", "wall_encountered", "wall_cleared", "directive_issued", "navigate", "pilot_step", "verdict", "journey_closed"] as const;

export type JourneyType = "signin";

export const JOURNEY_TYPES: Readonly<Record<JourneyType, { readonly verdictOutcomes: readonly string[] }>> = {
  "signin": { verdictOutcomes: ["verified", "attested_only", "unverified"] },
} as const;

export type SigninVerdictOutcome = "verified" | "attested_only" | "unverified";

export const JOURNEY_IDLE_CLOSE_MS = 600000;
export const JOURNEY_MAX_AGE_MS = 3600000;
export const OBSERVATION_FRESH_MS = 30000;
export const RECEIPT_SEQ_PAD = 6;

// ── Pure helpers ──────────────────────────────────────────────────────────
export function isTerminalControl(state: ControlState): boolean {
  return CONTROL_STATES[state]?.terminal === true;
}

export function canTransition(from: ControlState, to: ControlState): boolean {
  const targets = TRANSITIONS[from];
  return Array.isArray(targets) && targets.indexOf(to) !== -1;
}

export function riskClassOfVerb(verb: string): RiskClass | null {
  const def = (VERBS as Record<string, { riskClass: RiskClass } | undefined>)[verb];
  return def ? def.riskClass : null;
}

export function isBlackoutWall(wall: string | null | undefined): boolean {
  if (!wall) return false;
  const def = (WALL_TYPES as Record<string, { blackout: boolean } | undefined>)[wall];
  return def?.blackout === true;
}

export function wallForPageKind(kind: string | null | undefined): WallType | null {
  if (!kind) return null;
  const wall = (PAGE_KIND_TO_WALL as Record<string, WallType | null | undefined>)[kind];
  return wall ?? null;
}
